import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import api from "../Utils/Axios";
import * as XLSX from "xlsx";

function ProfitLossAccount() {
  const navigate = useNavigate();
  const [bills, setBills] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [companyLanguage, setCompanyLanguage] = useState('en');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    const lang = localStorage.getItem("companyLanguage") || 'en';
    setCompanyLanguage(lang);
    
    // Set default date range (current month)
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    setDateRange({
      startDate: firstDay.toISOString().split('T')[0],
      endDate: lastDay.toISOString().split('T')[0]
    });
    
    fetchData();
  }, []);

  const getToken = () => {
    return localStorage.getItem("companyToken");
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const token = getToken();
      if (!token) {
        navigate("/company/login");
        return;
      }

      // Fetch bills
      const billsResponse = await api.get('/bill/company/my-bills', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Fetch expenses from Expense Ledger
      const expensesResponse = await api.get('/expense-ledger/company/all', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const allBills = billsResponse.data.bills || [];
      const allExpenses = expensesResponse.data.expenses || [];
      
      setBills(allBills);
      setExpenses(allExpenses);
      
    } catch (error) {
      console.error('Fetch data error:', error);
      toast.error(companyLanguage === 'ur' ? 'ڈیٹا لوڈ نہیں ہو سکا' : 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const calculateProfitLoss = () => {
    // Filter by date range if provided
    const startDate = dateRange.startDate ? new Date(dateRange.startDate) : null;
    const endDate = dateRange.endDate ? new Date(dateRange.endDate) : null;
    
    // INCOME: Commission, Miscellaneous, Market Fee from bills
    let totalCommission = 0;
    let totalMiscellaneous = 0;
    let totalMarketFee = 0;
    
    bills.forEach(bill => {
      const billDate = new Date(bill.date);
      if (startDate && billDate < startDate) return;
      if (endDate && billDate > endDate) return;
      
      let expensesObj = bill.expenses || {};
      
      // Convert Map to object if needed
      if (expensesObj instanceof Map) {
        expensesObj = Object.fromEntries(expensesObj);
      } else if (expensesObj && typeof expensesObj === 'object' && expensesObj.get) {
        const plainObj = {};
        if (expensesObj.forEach) {
          expensesObj.forEach((val, key) => {
            plainObj[key] = val;
          });
        }
        expensesObj = plainObj;
      }
      
      // Get Commission
      const commission = Number(expensesObj['Commission'] || expensesObj['کمیشن'] || 0);
      totalCommission += commission;
      
      // Get Miscellaneous
      const miscellaneous = Number(expensesObj['Miscellaneous'] || expensesObj['مشیانہ'] || 0);
      totalMiscellaneous += miscellaneous;
      
      // Get Market Fee
      const marketFee = Number(expensesObj['Market Fee'] || expensesObj['مارکیٹ فیس'] || 0);
      totalMarketFee += marketFee;
    });
    
    const totalIncome = totalCommission + totalMiscellaneous + totalMarketFee;
    
    // EXPENSES: Rent and Labor from bills
    let totalRent = 0;
    let totalLabor = 0;
    
    bills.forEach(bill => {
      const billDate = new Date(bill.date);
      if (startDate && billDate < startDate) return;
      if (endDate && billDate > endDate) return;
      
      let expensesObj = bill.expenses || {};
      
      // Convert Map to object if needed
      if (expensesObj instanceof Map) {
        expensesObj = Object.fromEntries(expensesObj);
      } else if (expensesObj && typeof expensesObj === 'object' && expensesObj.get) {
        const plainObj = {};
        if (expensesObj.forEach) {
          expensesObj.forEach((val, key) => {
            plainObj[key] = val;
          });
        }
        expensesObj = plainObj;
      }
      
      // Get Rent
      const rent = Number(expensesObj['Rent'] || expensesObj['کرایہ'] || 0);
      totalRent += rent;
      
      // Get Labor
      const labor = Number(expensesObj['Labor'] || expensesObj['مزدوری'] || 0);
      totalLabor += labor;
    });
    
    // Other expenses from Expense Ledger
    let totalOtherExpenses = 0;
    expenses.forEach(expense => {
      const expenseDate = new Date(expense.date);
      if (startDate && expenseDate < startDate) return;
      if (endDate && expenseDate > endDate) return;
      
      const total = (expense.travellingExpense || 0) + 
                    (expense.refreshment || 0) + 
                    (expense.cargo || 0) + 
                    (expense.salaryExpense || 0);
      totalOtherExpenses += total;
    });
    
    const totalExpenses = totalRent + totalLabor + totalOtherExpenses;
    const netProfit = totalIncome - totalExpenses;
    
    return {
      income: {
        commission: totalCommission,
        miscellaneous: totalMiscellaneous,
        marketFee: totalMarketFee,
        total: totalIncome
      },
      expenses: {
        rent: totalRent,
        labor: totalLabor,
        other: totalOtherExpenses,
        total: totalExpenses
      },
      netProfit: netProfit
    };
  };

  const profitLoss = calculateProfitLoss();
  const isUrdu = companyLanguage === 'ur';

  const handleExportExcel = () => {
    try {
      const startDate = dateRange.startDate ? new Date(dateRange.startDate).toLocaleDateString() : 'All';
      const endDate = dateRange.endDate ? new Date(dateRange.endDate).toLocaleDateString() : 'All';
      
      // Prepare data for Excel
      const excelData = [
        // Header
        {
          [isUrdu ? 'منافع و نقصان کھاتہ' : 'Profit & Loss Account']: '',
          [isUrdu ? 'تفصیل' : 'Details']: '',
          [isUrdu ? 'رقم' : 'Amount']: ''
        },
        {
          [isUrdu ? 'منافع و نقصان کھاتہ' : 'Profit & Loss Account']: isUrdu ? 'مدت' : 'Period',
          [isUrdu ? 'تفصیل' : 'Details']: `${startDate} - ${endDate}`,
          [isUrdu ? 'رقم' : 'Amount']: ''
        },
        { [isUrdu ? 'منافع و نقصان کھاتہ' : 'Profit & Loss Account']: '', [isUrdu ? 'تفصیل' : 'Details']: '', [isUrdu ? 'رقم' : 'Amount']: '' },
        
        // Income Section
        { [isUrdu ? 'منافع و نقصان کھاتہ' : 'Profit & Loss Account']: isUrdu ? 'آمدنی' : 'INCOME', [isUrdu ? 'تفصیل' : 'Details']: '', [isUrdu ? 'رقم' : 'Amount']: '' },
        {
          [isUrdu ? 'منافع و نقصان کھاتہ' : 'Profit & Loss Account']: '',
          [isUrdu ? 'تفصیل' : 'Details']: isUrdu ? 'کمیشن' : 'Commission',
          [isUrdu ? 'رقم' : 'Amount']: profitLoss.income.commission
        },
        {
          [isUrdu ? 'منافع و نقصان کھاتہ' : 'Profit & Loss Account']: '',
          [isUrdu ? 'تفصیل' : 'Details']: isUrdu ? 'مشیانہ' : 'Miscellaneous',
          [isUrdu ? 'رقم' : 'Amount']: profitLoss.income.miscellaneous
        },
        {
          [isUrdu ? 'منافع و نقصان کھاتہ' : 'Profit & Loss Account']: '',
          [isUrdu ? 'تفصیل' : 'Details']: isUrdu ? 'مارکیٹ فیس' : 'Market Fee',
          [isUrdu ? 'رقم' : 'Amount']: profitLoss.income.marketFee
        },
        {
          [isUrdu ? 'منافع و نقصان کھاتہ' : 'Profit & Loss Account']: '',
          [isUrdu ? 'تفصیل' : 'Details']: isUrdu ? 'کل آمدنی' : 'Total Income',
          [isUrdu ? 'رقم' : 'Amount']: profitLoss.income.total
        },
        { [isUrdu ? 'منافع و نقصان کھاتہ' : 'Profit & Loss Account']: '', [isUrdu ? 'تفصیل' : 'Details']: '', [isUrdu ? 'رقم' : 'Amount']: '' },
        
        // Expenses Section
        { [isUrdu ? 'منافع و نقصان کھاتہ' : 'Profit & Loss Account']: isUrdu ? 'اخراجات' : 'EXPENSES', [isUrdu ? 'تفصیل' : 'Details']: '', [isUrdu ? 'رقم' : 'Amount']: '' },
        {
          [isUrdu ? 'منافع و نقصان کھاتہ' : 'Profit & Loss Account']: '',
          [isUrdu ? 'تفصیل' : 'Details']: isUrdu ? 'کرایہ' : 'Rent',
          [isUrdu ? 'رقم' : 'Amount']: profitLoss.expenses.rent
        },
        {
          [isUrdu ? 'منافع و نقصان کھاتہ' : 'Profit & Loss Account']: '',
          [isUrdu ? 'تفصیل' : 'Details']: isUrdu ? 'مزدوری' : 'Labor',
          [isUrdu ? 'رقم' : 'Amount']: profitLoss.expenses.labor
        },
        {
          [isUrdu ? 'منافع و نقصان کھاتہ' : 'Profit & Loss Account']: '',
          [isUrdu ? 'تفصیل' : 'Details']: isUrdu ? 'دیگر اخراجات' : 'Other Expenses',
          [isUrdu ? 'رقم' : 'Amount']: profitLoss.expenses.other
        },
        {
          [isUrdu ? 'منافع و نقصان کھاتہ' : 'Profit & Loss Account']: '',
          [isUrdu ? 'تفصیل' : 'Details']: isUrdu ? 'کل اخراجات' : 'Total Expenses',
          [isUrdu ? 'رقم' : 'Amount']: profitLoss.expenses.total
        },
        { [isUrdu ? 'منافع و نقصان کھاتہ' : 'Profit & Loss Account']: '', [isUrdu ? 'تفصیل' : 'Details']: '', [isUrdu ? 'رقم' : 'Amount']: '' },
        
        // Net Profit/Loss
        {
          [isUrdu ? 'منافع و نقصان کھاتہ' : 'Profit & Loss Account']: '',
          [isUrdu ? 'تفصیل' : 'Details']: profitLoss.netProfit >= 0 
            ? (isUrdu ? 'خالص منافع' : 'Net Profit')
            : (isUrdu ? 'خالص نقصان' : 'Net Loss'),
          [isUrdu ? 'رقم' : 'Amount']: profitLoss.netProfit
        }
      ];

      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, isUrdu ? 'منافع و نقصان' : 'Profit & Loss');

      // Generate filename with date range
      const filename = `Profit-Loss-Account-${dateRange.startDate || 'all'}-${dateRange.endDate || 'all'}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);
      toast.success(isUrdu ? 'Excel فائل ڈاؤن لوڈ ہو گئی' : "Excel file downloaded successfully");
    } catch (error) {
      console.error("Export Excel error:", error);
      toast.error(isUrdu ? 'Excel فائل بنانے میں ناکام' : "Failed to export Excel file");
    }
  };

  return (
    <>
      <Helmet><title>{isUrdu ? 'منافع و نقصان کھاتہ' : 'Profit & Loss Account'} - Company Dashboard</title></Helmet>
      
      <div className={`min-h-screen bg-brand-background p-4 md:p-8 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}} dir={isUrdu ? 'rtl' : 'ltr'}>
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="bg-gradient-to-r from-brand-header to-emerald-800 rounded-xl shadow-lg p-6 mb-6 text-white">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h1 className={`text-3xl font-bold mb-2 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  {isUrdu ? 'منافع و نقصان کھاتہ' : 'Profit & Loss Account'}
                </h1>
                <p className="text-white/80 text-sm">
                  {isUrdu ? 'کمپنی کی آمدنی اور اخراجات کا حساب' : 'Company Income and Expenses Statement'}
                </p>
              </div>
            </div>
          </div>

          {/* Date Range Filter */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6 border border-brand-card">
            <div className={`flex items-center gap-4 flex-wrap ${isUrdu ? 'flex-row-reverse' : ''}`}>
              <div className="flex-1 min-w-[200px]">
                <label className={`block text-sm font-medium text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  {isUrdu ? 'شروع کی تاریخ:' : 'Start Date:'}
                </label>
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent"
                />
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className={`block text-sm font-medium text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  {isUrdu ? 'اختتام کی تاریخ:' : 'End Date:'}
                </label>
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent"
                />
              </div>
              <div className="flex items-end gap-3">
                <button
                  onClick={handleExportExcel}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 py-2 rounded-lg transition-all transform hover:scale-105 flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {isUrdu ? 'Excel ڈاؤن لوڈ' : 'Export Excel'}
                </button>
                <button
                  onClick={fetchData}
                  className="bg-brand-header hover:bg-emerald-800 text-white font-semibold px-6 py-2 rounded-lg transition-all transform hover:scale-105"
                >
                  {isUrdu ? 'تازہ کریں' : 'Refresh'}
                </button>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="text-center py-12">
              <p className={`text-gray-500 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                {isUrdu ? 'لوڈ ہو رہا ہے...' : 'Loading...'}
              </p>
            </div>
          ) : (
            <>
              {/* Summary Cards */}
              <div className="grid md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg shadow-lg p-6 text-white">
                  <div className={`text-sm opacity-90 mb-2 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                    {isUrdu ? 'کل آمدنی' : 'Total Income'}
                  </div>
                  <div className="text-3xl font-bold">
                    {profitLoss.income.total.toLocaleString()}
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg shadow-lg p-6 text-white">
                  <div className={`text-sm opacity-90 mb-2 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                    {isUrdu ? 'کل اخراجات' : 'Total Expenses'}
                  </div>
                  <div className="text-3xl font-bold">
                    {profitLoss.expenses.total.toLocaleString()}
                  </div>
                </div>
                
                <div className={`bg-gradient-to-br ${profitLoss.netProfit >= 0 ? 'from-green-500 to-emerald-600' : 'from-red-500 to-red-600'} rounded-lg shadow-lg p-6 text-white`}>
                  <div className={`text-sm opacity-90 mb-2 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                    {profitLoss.netProfit >= 0 
                      ? (isUrdu ? 'خالص منافع' : 'Net Profit')
                      : (isUrdu ? 'خالص نقصان' : 'Net Loss')
                    }
                  </div>
                  <div className="text-3xl font-bold">
                    {profitLoss.netProfit >= 0 ? '+' : ''}{profitLoss.netProfit.toLocaleString()}
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
                  <div className={`text-sm opacity-90 mb-2 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                    {isUrdu ? 'منافع کی شرح' : 'Profit Margin'}
                  </div>
                  <div className="text-3xl font-bold">
                    {profitLoss.income.total > 0 
                      ? ((profitLoss.netProfit / profitLoss.income.total) * 100).toFixed(1)
                      : 0}%
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
              {/* Income Section */}
              <div className="bg-white rounded-lg shadow-lg p-6 border border-brand-card">
                <h2 className={`text-2xl font-bold text-brand-header mb-6 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  {isUrdu ? 'آمدنی' : 'Income'}
                </h2>
                
                <div className="space-y-4">
                  <div className={`flex justify-between items-center py-3 border-b border-gray-200 ${isUrdu ? 'flex-row-reverse' : ''}`}>
                    <span className={`text-gray-700 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                      {isUrdu ? 'کمیشن' : 'Commission'}
                    </span>
                    <span className="font-bold text-green-600 text-lg">
                      {profitLoss.income.commission.toLocaleString()}
                    </span>
                  </div>
                  
                  <div className={`flex justify-between items-center py-3 border-b border-gray-200 ${isUrdu ? 'flex-row-reverse' : ''}`}>
                    <span className={`text-gray-700 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                      {isUrdu ? 'مشیانہ' : 'Miscellaneous'}
                    </span>
                    <span className="font-bold text-green-600 text-lg">
                      {profitLoss.income.miscellaneous.toLocaleString()}
                    </span>
                  </div>
                  
                  <div className={`flex justify-between items-center py-3 border-b border-gray-200 ${isUrdu ? 'flex-row-reverse' : ''}`}>
                    <span className={`text-gray-700 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                      {isUrdu ? 'مارکیٹ فیس' : 'Market Fee'}
                    </span>
                    <span className="font-bold text-green-600 text-lg">
                      {profitLoss.income.marketFee.toLocaleString()}
                    </span>
                  </div>
                  
                  <div className={`flex justify-between items-center py-4 border-t-2 border-brand-header mt-4 ${isUrdu ? 'flex-row-reverse' : ''}`}>
                    <span className={`text-xl font-bold text-brand-header ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                      {isUrdu ? 'کل آمدنی' : 'Total Income'}
                    </span>
                    <span className="text-2xl font-bold text-green-600">
                      {profitLoss.income.total.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Expenses Section */}
              <div className="bg-white rounded-lg shadow-lg p-6 border border-brand-card">
                <h2 className={`text-2xl font-bold text-red-600 mb-6 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  {isUrdu ? 'اخراجات' : 'Expenses'}
                </h2>
                
                <div className="space-y-4">
                  <div className={`flex justify-between items-center py-3 border-b border-gray-200 ${isUrdu ? 'flex-row-reverse' : ''}`}>
                    <span className={`text-gray-700 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                      {isUrdu ? 'کرایہ' : 'Rent'}
                    </span>
                    <span className="font-bold text-red-600 text-lg">
                      {profitLoss.expenses.rent.toLocaleString()}
                    </span>
                  </div>
                  
                  <div className={`flex justify-between items-center py-3 border-b border-gray-200 ${isUrdu ? 'flex-row-reverse' : ''}`}>
                    <span className={`text-gray-700 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                      {isUrdu ? 'مزدوری' : 'Labor'}
                    </span>
                    <span className="font-bold text-red-600 text-lg">
                      {profitLoss.expenses.labor.toLocaleString()}
                    </span>
                  </div>
                  
                  <div className={`flex justify-between items-center py-3 border-b border-gray-200 ${isUrdu ? 'flex-row-reverse' : ''}`}>
                    <span className={`text-gray-700 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                      {isUrdu ? 'دیگر اخراجات' : 'Other Expenses'}
                    </span>
                    <span className="font-bold text-red-600 text-lg">
                      {profitLoss.expenses.other.toLocaleString()}
                    </span>
                  </div>
                  
                  <div className={`flex justify-between items-center py-4 border-t-2 border-red-600 mt-4 ${isUrdu ? 'flex-row-reverse' : ''}`}>
                    <span className={`text-xl font-bold text-red-600 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                      {isUrdu ? 'کل اخراجات' : 'Total Expenses'}
                    </span>
                    <span className="text-2xl font-bold text-red-600">
                      {profitLoss.expenses.total.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Net Profit/Loss */}
            <div className={`mt-6 bg-white rounded-lg shadow-lg p-6 border-2 ${profitLoss.netProfit >= 0 ? 'border-green-500' : 'border-red-500'} ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
              <div className={`flex justify-between items-center ${isUrdu ? 'flex-row-reverse' : ''}`}>
                <h2 className={`text-2xl font-bold ${profitLoss.netProfit >= 0 ? 'text-green-600' : 'text-red-600'} ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  {profitLoss.netProfit >= 0 
                    ? (isUrdu ? 'خالص منافع' : 'Net Profit')
                    : (isUrdu ? 'خالص نقصان' : 'Net Loss')
                  }
                </h2>
                <span className={`text-4xl font-bold ${profitLoss.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {profitLoss.netProfit >= 0 ? '+' : ''}{profitLoss.netProfit.toLocaleString()}
                </span>
              </div>
            </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default ProfitLossAccount;

